import { ResponsiveTable, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';

import type { MoneyFormatters } from '../../_money/useMoneyFormatters.js';
import { MoreMenu } from '../../_ops/MoreMenu.js';
import type { EmployeeListItemDto } from '../contractsMirror.js';
import { EmployeeStatusBadge } from './EmployeeStatusBadge.js';

/**
 * Employees list table (legacy `EmployeesPage` table, redesigned to the locked
 * brand — archetype A list). A real semantic `<table>` — Name · Pay Type · **Rate**
 * (right, mono) · Contact · Status (badge) · Actions — with a `<caption>`,
 * `<th scope="col">` headers, the numeric rate right-aligned, and a readable empty
 * row (A11Y-01). Numerals in IBM Plex Mono. Every color is a locked token (BRAND-06).
 *
 * The name cell links to the detail route. The row-action `MoreMenu` (Shared `_ops`)
 * offers View / Edit / Deactivate-or-Restore; the destructive item routes through the
 * caller's `ConfirmDialog` (Shared 7 — never a native confirm).
 */

const TEXT_TH =
  'px-3 py-2 text-left font-semibold text-muted-foreground text-xs uppercase tracking-wide';
const NUMERIC_TH =
  'px-3 py-2 text-right font-semibold text-muted-foreground text-xs uppercase tracking-wide';

function Dash() {
  return <span className="text-muted-foreground">—</span>;
}

/** The authoritative pay rate for the employee's pay basis, formatted for display. */
function payRateLabel(
  employee: EmployeeListItemDto,
  fmt: MoneyFormatters,
  t: (key: string) => string,
): string {
  switch (String(employee.payType).toLowerCase()) {
    case 'salary':
      return employee.annualSalary ? `${fmt.formatCurrency(employee.annualSalary)}/yr` : '—';
    case 'weekly':
      return employee.weeklySalary ? `${fmt.formatCurrency(employee.weeklySalary)}/wk` : '—';
    default:
      return employee.hourlyRate ? `${fmt.formatCurrency(employee.hourlyRate)}/hr` : '—';
  }
}

export function EmployeesTable({
  employees,
  fmt,
  onView,
  onEdit,
  onDeactivate,
}: {
  employees: EmployeeListItemDto[];
  fmt: MoneyFormatters;
  /** In-SPA navigation to the employee detail route (router `navigate`). */
  onView: (employee: EmployeeListItemDto) => void;
  /** In-SPA navigation to the employee edit route. Omit it and the menu offers no Edit. */
  onEdit?: (employee: EmployeeListItemDto) => void;
  /** Opens the caller's deactivate/restore confirm. Omit it and the menu offers neither. */
  onDeactivate?: (employee: EmployeeListItemDto) => void;
}) {
  const { t } = useTranslation();

  return (
    <ResponsiveTable className="rounded-md border border-border bg-card">
      <table className="w-full border-collapse">
        <caption className="sr-only">{t('employees:table.caption')}</caption>
        <thead>
          <tr className="border-border border-b">
            <th scope="col" className={TEXT_TH}>
              {t('employees:table.name')}
            </th>
            <th scope="col" className={TEXT_TH}>
              {t('employees:table.payType')}
            </th>
            <th scope="col" className={NUMERIC_TH}>
              {t('employees:table.rate')}
            </th>
            <th scope="col" className={TEXT_TH}>
              {t('employees:table.contact')}
            </th>
            <th scope="col" className={TEXT_TH}>
              {t('employees:table.status')}
            </th>
            <th scope="col" className={NUMERIC_TH}>
              {t('employees:table.actions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {employees.map((employee) => (
            <tr key={employee.id} className="border-border/60 border-b last:border-b-0">
              <td className="px-3 py-2 text-sm">
                <Link
                  to="/employees/$id"
                  params={{ id: String(employee.id) }}
                  className="font-semibold text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  {employee.name}
                </Link>
              </td>
              <td className="px-3 py-2 text-foreground text-sm">
                {t(`employees:payType.${String(employee.payType).toLowerCase()}`, {
                  defaultValue: employee.payType,
                })}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-right font-mono font-medium text-foreground text-sm">
                {payRateLabel(employee, fmt, t)}
              </td>
              <td className="px-3 py-2 text-foreground text-sm">
                {employee.email || employee.phone ? (
                  <div className="flex flex-col">
                    {employee.email ? <span>{employee.email}</span> : null}
                    {employee.phone ? (
                      <span className="font-mono text-muted-foreground text-xs">
                        {employee.phone}
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <Dash />
                )}
              </td>
              <td className="px-3 py-2 text-sm">
                <EmployeeStatusBadge employee={employee} />
              </td>
              <td className="px-3 py-2 text-right">
                <div className="flex justify-end">
                  <MoreMenu
                    label={t('employees:actions.menuLabel', { name: employee.name })}
                    items={[
                      {
                        key: 'view',
                        label: t('employees:actions.view'),
                        onSelect: () => onView(employee),
                      },
                      ...(onEdit
                        ? [
                            {
                              key: 'edit',
                              label: t('employees:actions.editShort'),
                              onSelect: () => onEdit(employee),
                            },
                          ]
                        : []),
                      ...(onDeactivate
                        ? [
                            {
                              key: 'deactivate',
                              label: employee.archivedAt
                                ? t('employees:actions.restore')
                                : t('employees:actions.deactivate'),
                              destructive: !employee.archivedAt,
                              onSelect: () => onDeactivate(employee),
                            },
                          ]
                        : []),
                    ]}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ResponsiveTable>
  );
}
